import moment, { duration, ISO_8601, utc } from 'moment';

import { DayOfWeek } from '@shared/models';

export function now(): Date {
  return moment().toDate();
}

export function toUtc(value?: Date): Date | null {
  if (value == null) return null;
  return utc(value).local().toDate();
}

export function toDate(value?: string | Date | unknown): Date {
  return moment(value).toDate();
}

export function toUtcString(value: Date): string {
  const date = toUtc(value);
  return moment(date).format('YYYY-MM-DD HH:mm');
}

export function fromDayString(value: string): Date {
  return moment(value, 'YYYY-MM-DD', true).toDate();
}

export function format(value: string, targetFormat: string, originalFormat: string): string {
  const parsed = utc(value, originalFormat, true);
  return parsed.isValid() ? parsed.format(targetFormat) : value;
}

export function toDayString(value: Date): string {
  const date = toUtc(value);
  return moment(date).format('YYYY-MM-DD');
}

export function toMonthString(value: Date): string {
  const date = toUtc(value);
  return moment(date).format('YYYY-MM');
}

export function isStringISOValid(date: string) {
  return moment(date, ISO_8601, true).isValid();
}

export function toLocalUnix(date: Date) {
  return utc(date).local().unix() * 1000;
}

export function toLocal(date: Date) {
  return utc(date).local();
}

export function isBetween(date: string | Date, from: string | Date, to: string | Date): boolean {
  return isAfterOrEqual(date, from) && isBeforeOrEqual(date, to);
}

export function isEqual(date: string | Date, from: string | Date): boolean {
  return moment(date).isSame(moment(from));
}

export function isAfterOrEqual(date1: Date | string, date2: Date | string): boolean {
  return moment(date1).isSameOrAfter(moment(date2), 'day');
}

export function isBeforeOrEqual(date1: Date | string, date2: Date | string): boolean {
  return moment(date1).isSameOrBefore(moment(date2), 'day');
}

export function isBetweenOrEqual(date: string | Date, from: string | Date, to: string | Date): boolean {
  return isBetween(date, from, to) || moment(date).isSame(moment(from)) || moment(date).isSame(moment(to));
}

export function isWeekInDateRange(week: string, startDate: Date | string, endDate: Date | string): boolean {
  return toDayString(toDate(startDate)) < toDayString(addWeeks(week, 1)) && toDayString(toDate(endDate)) >= week;
}

export function getDateDifference(from: Date | string, to: Date | string) {
  return duration(moment(to).diff(moment(from)));
}

export function getYearsDifference(startDate: Date, endDate: Date): number {
  return moment(endDate).diff(moment(startDate), 'years', true);
}

export function getMonthDifference(startDate: Date, endDate: Date): number {
  return moment(endDate).diff(moment(startDate), 'months');
}

export function getWeeksDifference(startDate: Date, endDate: Date): number {
  return moment(endDate).diff(moment(startDate), 'weeks');
}

export function getCurrentWeekDateRange(): Date[] {
  return [getFirstDayOfWeek(), getLastDayOfWeek()];
}

export function getWeekDateRange(date): Date[] {
  return [getFirstDayOfWeek(date), getLastDayOfWeek(date)];
}

export function getCurrentMonthDateRange(date?: Date): Date[] {
  return [getFirstDayOfMonth(date), getLastDayOfMonth(date)];
}

export function getFromNowTillEndOfWeekDateRange(): Date[] {
  return [moment().toDate(), getLastDayOfWeek()];
}

export function getCurrentTwoWeeksDateRange(): Date[] {
  return [getFirstDayOfWeek(moment().subtract(7, 'days').toDate()), getLastDayOfWeek()];
}

export function getDaysBetween(start: string | Date, end: string | Date): string[] {
  const startDate = moment(start).startOf('day');
  const endDate = moment(end).startOf('day');
  const days: moment.Moment[] = [];

  const current = startDate.clone();
  while (current.isSameOrBefore(endDate, 'day')) {
    days.push(current.clone());
    current.add(1, 'day');
  }

  return days.map((d) => toDayString(d.toDate()));
}

export function getFirstDayOfMonth(date?: Date): Date {
  return getMinTime(moment(date).startOf('month').toDate());
}

export function getLastDayOfMonth(date?: Date): Date {
  return getMaxTime(moment(date).endOf('month').toDate());
}

export function getFirstDayOfWeek(date?: Date | string): Date {
  return getMinTime(moment(date).weekday(1).toDate());
}

export function getLastDayOfWeek(date?: Date | string): Date {
  return getMaxTime(moment(date).weekday(7).toDate());
}

export function getMinTime(date: Date): Date {
  return moment(date).startOf('day').toDate();
}

export function getMaxTime(date: Date): Date {
  return moment(date).endOf('day').toDate();
}

export function getLastDayOfYear(date?: Date | string): Date {
  return getMaxTime(moment(date).endOf('year').toDate());
}

export function addHours(date: Date, count: number): Date {
  return moment(date).add(count, 'hours').toDate();
}

export function addDays(date: Date, count: number): Date {
  return moment(date).add(count, 'days').toDate();
}

export function addWeeks(date: Date | string, count: number): Date {
  return moment(date).add(count, 'weeks').toDate();
}

export function addMonths(date: Date, count: number): Date {
  return moment(date).add(count, 'months').toDate();
}

export function addYears(date: Date, years: number): Date {
  return moment(date).add(years, 'year').toDate();
}

export function getIsoWeekOfYear(date: Date): number {
  return moment(date).isoWeek();
}

export function getIsoWeekAndYear(date: Date | string): string {
  return moment(date).format('WW/GGGG');
}

export function compareDatesDescending(a: string | Date, b: string | Date): number {
  const dateA = moment(a);
  const dateB = moment(b);

  if (!dateA.isValid() || !dateB.isValid()) {
    return 0;
  }

  return dateB.diff(dateA);
}

export function getDurationBetweenDates(from: Date | string, to: Date | string): moment.Duration | null {
  const start = moment(from);
  const end = moment(to);
  if (!start.isValid() || !end.isValid()) {
    return null;
  }
  return duration(end.diff(start));
}

export function getDurationWeeks(startDate: Date | undefined, endDate: Date | undefined): number | undefined {
  if (!startDate || !endDate) {
    return undefined;
  }

  return Math.round(getDateDifference(startDate, endDate).asWeeks()) + 1;
}

export function getDurationDays(startDate: Date | undefined, endDate: Date | undefined): number | undefined {
  if (!startDate || !endDate) {
    return undefined;
  }

  return Math.round(getDateDifference(startDate, endDate).asDays()) + 1;
}

export function formatMonthYear(date: Date | string): string {
  return moment(date).format('MM.YY');
}

export function getWeeks(startDate: Date | string, endDate: Date | string): string[] {
  const weeksCount = Math.round((getDateDifference(startDate, endDate).asWeeks() || 0) + 1);
  const weeks = new Array(weeksCount).fill(null).map((_, i) => i);

  return weeks.map((index) => {
    const date = addWeeks(startDate, index);
    const startWeekDate = getFirstDayOfWeek(date);
    return toDayString(startWeekDate);
  });
}

export function getDayOfWeekFromDate(date: Date | string): DayOfWeek {
  const d = moment(date).isoWeekday() % 7;
  return d as DayOfWeek;
}

export function isWeekend(date: Date | string): boolean {
  const dayOfWeek = getDayOfWeekFromDate(date);
  return dayOfWeek === DayOfWeek.Saturday || dayOfWeek === DayOfWeek.Sunday;
}
